//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';
import { IUrlRouterProvider } from 'angular-ui-router';

import {
  IPartialState,
  NavGroup,
  StateFactory,
  componentNameToTemplate,
  hasAllFeatures,
  hasFeature,
  lazyLoadModule,
} from 'app/initialization/routing/stateHelpers';

// Sets default values for state data
function TikTokStateFactory(state: IPartialState) {
  const accessFn = state.data?.hasAccess ?? (() => true);
  const data = {
    title: 'TikTok Account',
    navGroup: NavGroup.PUBLISHING,
    ...state.data,
    hasAccess: () =>
      hasAllFeatures(FeatureNames.Social, FeatureNames.SocialDashboards) &&
      accessFn(),
  };

  return StateFactory({
    ...state,
    data,
  });
}

export function routingRules(urlRouterProvider: IUrlRouterProvider) {
  urlRouterProvider.when('/tiktok/schedule', '/tiktok/schedule/calendar');
  urlRouterProvider.when('/tiktok/schedule/calendar', '/schedule/calendar');
  urlRouterProvider.when(
    '/tiktok/schedule/post-details',
    '/schedule/scheduler',
  );
  urlRouterProvider.when('/tiktok/drafts', '/schedule/drafts');

  // default to opening moderation page - /tiktok/like2buy/preview should only be accessed via the Preview button
  // in moderation page
  urlRouterProvider.when('/tiktok/like2buy', '/tiktok/like2buy/moderation');

  if (!hasFeature(FeatureNames.Like2BuyTiktok)) {
    urlRouterProvider.when(/\/tiktok\/like2buy\/.*/, '/tiktok/summary');
  }
}

export const states = {
  tiktok: TikTokStateFactory({
    url: '/tiktok',
    template: componentNameToTemplate('tiktokPage'),
    redirectTo: 'tiktok.summary',
    resolve: {
      ...lazyLoadModule('curalate.tiktok', () => import('@tiktok/TikTokApp')),
    },
  }),

  'tiktok.audience': TikTokStateFactory({
    url: '/audience',
    views: {
      'tiktok-header': {
        template: `<tiktok-header title="Audience" role="heading" aria-level="1"></tiktok-header>`,
      },
      'tiktok-content': {
        template: componentNameToTemplate('tiktokAudience'),
      },
    },
  }),

  'tiktok.engagement': TikTokStateFactory({
    url: '/engagement',
    views: {
      'tiktok-header': {
        template: `<tiktok-header title="Engagement" role="heading" aria-level="1"></tiktok-header>`,
      },
      'tiktok-content': {
        template: componentNameToTemplate('tiktokEngagement'),
      },
    },
  }),

  'tiktok.summary': TikTokStateFactory({
    url: '/summary',
    views: {
      'tiktok-header': {
        template: `<tiktok-summary-header></tiktok-summary-header>`,
      },
      'tiktok-content': {
        template: componentNameToTemplate('tiktokSummary'),
      },
    },
  }),

  'tiktok.mentions': TikTokStateFactory({
    url: '/mentions',
    data: {
      title: 'TikTok Mentions',
      hasAccess: () => hasFeature(FeatureNames.TiktokMentionedMediaIngestion),
    },
    views: {
      main: {
        template: componentNameToTemplate('tiktokPage'),
      },
    },
  }),

  'tiktok.like2buy': StateFactory({
    url: '/like2buy',
    data: {
      title: 'TikTok Like2Buy',
      hasAccess: () => hasFeature(FeatureNames.Like2BuyTiktok),
      navGroup: NavGroup.COMMERCE,
    },
    views: {
      'tiktok-header': {
        template:
          '<div ui-view="tiktok-like2buy-header" class="flex-tall cu-h-100"></div>',
      },
      'tiktok-content': {
        template:
          '<div ui-view="tiktok-like2buy-sub" class="flex-tall cu-h-100"></div>',
      },
    },
  }),
  'tiktok.like2buy.moderation': StateFactory({
    url: '/moderation',
    data: {
      title: 'TikTok Like2Buy Moderation',
      navGroup: NavGroup.COMMERCE,
    },
    views: {
      'tiktok-like2buy-header': {
        template: '<tiktok-like2buy-header></tiktok-like2buy-header>',
      },
      'tiktok-like2buy-sub': {
        template: '<tiktok-like2buy></tiktok-like2buy>',
      },
    },
  }),
  'tiktok.like2buy.preview': StateFactory({
    url: '/preview',
    data: {
      title: 'TikTok Like2Buy Preview',
      navGroup: NavGroup.COMMERCE,
    },
    views: {
      'tiktok-like2buy-header': {
        template:
          '<tiktok-like2buy-preview-header></tiktok-like2buy-preview-header>',
      },
      'tiktok-like2buy-sub': {
        template: componentNameToTemplate('tiktokLike2BuyPreviewPage'),
      },
    },
  }),
};
